import React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import useToast from "hooks/useToast";
import { lccStore } from "./LCCStore";
import { buildingStore } from "store/Building/BuildingStore";
import { conformityStore } from "store/ConformityStore";
import Totals from "./Totals";
import ConventionSettings from "./ConventionSettings";
import LCCDataTable from "./LCCDataTable";
import EmptyState from "components/EmptyState";

const LCCPage = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();

  React.useEffect(() => {
    lccStore.loadData();

    return () => {
      lccStore.reset();
    };
  }, [buildingStore.currentBuilding?.id]);

  React.useEffect(() => {
    if (lccStore.error) {
      toast(lccStore.error, "warning");
      lccStore.setError(null);
    }
  }, [lccStore.error]);

  const building = buildingStore.currentBuilding;
  const certificationName = conformityStore.certificationOptions.find(
    (c) => c.id === building?.certification_id
  )?.name_en;

  if (!buildingStore.buildingMappingStatus.hasReport) {
    return (
      <EmptyState
        type="inventory"
        loading={buildingStore.buildingMappingStatus.loading}
        containerClassName="m-6"
      />
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {t("lcc.lifecycleCosting")}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t("lcc.lifecycleCostingSubtitle")}
          </p>
        </div>
        <div className="flex gap-6 text-sm text-gray-600 shrink-0">
          {certificationName && (
            <div className="text-right">
              <div className="text-xs text-gray-400">
                {t("lcc.certification")}
              </div>
              <div className="font-medium text-gray-800">
                {certificationName}
              </div>
            </div>
          )}
          {building?.gross_floor_area != null && (
            <div className="text-right">
              <div className="text-xs text-gray-400">BGF</div>
              <div className="font-medium text-gray-800">
                {building.gross_floor_area.toLocaleString(i18n.language)} m²
              </div>
            </div>
          )}
          {building?.life_span != null && (
            <div className="text-right">
              <div className="text-xs text-gray-400">{t("lcc.lifeSpan")}</div>
              <div className="font-medium text-gray-800">
                {building.life_span} {t("lcc.years")}
              </div>
            </div>
          )}
        </div>
      </div>
      <ConventionSettings />
      <Totals />
      <LCCDataTable />
    </div>
  );
};

export default observer(LCCPage);
